import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Navigate, Outlet, createRootRouteWithContext, useLocation, useRouterState } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { useAuth } from '@vizit/auth';
import { BackToNavbar } from '@vizit/organisms';
import type { RouteContext } from '../router';
import { useLegacyWorkspaceRedirect } from '../router/route-utils/legacy-redirect';

export const Route = createRootRouteWithContext<RouteContext>()({
  staticData: {
    pageName: '',
  },
  component: RouteComponent,
  notFoundComponent: NotFound,
});

function RouteComponent() {
  return (
    <>
      <main>
        <Outlet />
      </main>
      <TanStackRouterDevtools />
      <ReactQueryDevtools position="right" />
    </>
  );
}

function NotFound() {
  const { isAuthenticated } = useAuth();
  const { pathname, search } = useLocation();
  const legacyRedirect = useLegacyWorkspaceRedirect(pathname, search);
  const isRouterSettling = useRouterState({ select: (state) => state.isLoading || state.status === 'pending' });

  if (isRouterSettling) {
    return null;
  }

  if (isAuthenticated && legacyRedirect.status === 'resolving') {
    return null;
  }

  if (isAuthenticated && legacyRedirect.status === 'redirect') {
    return <Navigate to={legacyRedirect.to} search={legacyRedirect.search} replace />;
  }

  if (!isAuthenticated) {
    return (
      <div className="h-screen flex flex-col bg-white">
        <div className="flex flex-col items-center justify-center h-full">
          <h1 className="text-display-2xl font-bold">404</h1>
          <p className="text-body-lg">Page not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <BackToNavbar previousPageName="Home" to="/" />
      <div className="bg-white flex-grow w-full content-center">
        <div className="flex flex-col items-center justify-center h-full">
          <h1 className="text-display-2xl font-bold">404</h1>
          <p className="text-body-lg">Page not found</p>
        </div>
      </div>
    </div>
  );
}

export type { RouteContext };
